/**
 * Full-page Dash-style CreateJob form at /jobs/new.
 *
 * Multi-division behavior: no matter how many divisions are checked, ONE job
 * is created. The first division is primary and drives the YY-NNNN-DIV job
 * number; every checked division becomes a job_divisions row on that same job
 * (each displayable as YY-NNNN-<DIV> with the shared base number).
 *
 * Parity with the job page (FB13-FB51): every control here is the SAME
 * component the job page uses, so intake asks the questions in the same words
 * and writes them to the same columns —
 *
 *   Customer  first/last name, Occupancy (FB18), Year Built with the assessor
 *             auto-lookup (FB27), the State-from-phone suggestion (FB23), the
 *             Self Pay slider (FB24) and Different Billing Address (FB25/FB51).
 *   Job       Lead Channel → Lead Source (FB29b), Source of Damage (FB31),
 *             Type of Loss (FB46), Date of Loss with FB37's one-click fill, and
 *             the FB34/FB35 scoping facts feeding the Job Size suggestion.
 *   Dispatch  Emergency / Schedule / Mold with the FB13 gauntlet (FB13/FB36).
 *   Summary   Initial Call Summary (FB14), the field the job page shows.
 *
 * Sections run Customer → Job → Dispatch → Initial Call Summary, matching the
 * job page's card order; the Dash-era blocks (participants, policy, payment)
 * follow underneath.
 */

import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { isDashboardMode } from '~/lib/mode';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Field, Input, Select, Textarea, StatusPill } from '~/components/ui';
import { supabase } from '~/lib/supabase';
import { apiSend } from '~/lib/api/_client';
import { useAuth } from '~/lib/auth';
import { DIVISIONS, DIVISION_LABELS, formatDateTime } from '~/lib/format';
import { customerNameParts } from '~/lib/customerName';
import {
  EXTERNAL_ROLE_SLOTS, INTERNAL_ROLE_KEYS,
  applyJobPrefill, makeInitialForm, occupancyOf, validate,
  type CreateJobForm, type PhoneSet,
} from '~/components/job/create/form-state';
import { buildCreatePayload } from '~/components/job/create/create-payload';
import { useJob } from '~/hooks/useJob';
import { Section, Grid } from '~/components/job/create/Section';
import { LeadChannelRow, LeadSourceRow } from '~/components/job/lead-fields';
import { DamageCauseRow } from '~/components/job/damage-cause-field';
import { LOSS_TYPES, LEGACY_LOSS_TYPE, lossTypeSelectValue } from '~/components/job/loss-type-options';
import { OccupancyChoice } from '~/components/job/occupancy-row';
import { ToggleRow } from '~/components/job/InfoRow';
import { YearBuiltRow } from '~/components/job/YearBuiltRow';
import { stateFromPhones } from '~/components/job/state-from-phone';
import { toDateInput } from '~/components/job/dates-defs';
import { CreateDispatchRow } from '~/components/job/create/CreateDispatchRow';
import { ScopingFacts } from '~/components/job/create/ScopingFacts';
import {
  BillingAddressFields, BillingAddressToggle,
} from '~/components/job/create/BillingAddressFields';
import { PhonesEditor } from '~/components/job/create/PhonesEditor';
import { ExternalContactPicker } from '~/components/job/create/ExternalContactPicker';
import { CustomerSearchInline } from '~/components/job/create/CustomerSearchInline';
import type { PropertyAddress } from '~/lib/api/property';

/**
 * Optional search params used to PRELOAD the form from another surface (e.g. the
 * Dispatch call popup's "Create Job File" button). All fields are optional, so
 * /jobs/new with no params behaves exactly as before.
 *
 * `loss_description` is the RingCentral AI call summary the dispatch popup
 * passes; it seeds the Initial Call Summary (FB14), which is where a call
 * summary belongs now that the field is named after what it holds. The param
 * keeps its old name so the existing callers (web + mobile dispatch screens)
 * don't have to change in lockstep.
 *
 * `from` is the FB18 associated-job flow: `?from=26-1273-WTR` loads that job's
 * bundle and copies every job/loss fact onto this form while leaving the
 * customer block blank (see applyJobPrefill). It wins over the customer_* seeds
 * — the whole point of the flow is that the customer is somebody else.
 */
interface NewJobSearch {
  customer_first_name?: string;
  customer_last_name?: string;
  customer_phone?: string;
  customer_address1?: string;
  customer_city?: string;
  customer_state?: string;
  customer_zip?: string;
  /** The AI call summary — seeds the Initial Call Summary (jobs.job_description). */
  call_summary?: string;
  /** Legacy spelling of the same thing, kept so old links keep working. */
  loss_description?: string;
  /** public.call_log id — linked to the job and stamped onto jobs.rc_call_id. */
  call_log_id?: string;
  from?: string;
}

function str(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined;
  const t = v.trim();
  return t ? t : undefined;
}

export const Route = createFileRoute('/_app/jobs/new')({
  beforeLoad: () => {
    // Job creation lives in Dash while the app runs as the read-only dashboard.
    if (isDashboardMode()) throw redirect({ to: '/jobs' });
  },
  validateSearch: (search: Record<string, unknown>): NewJobSearch => ({
    customer_first_name: str(search.customer_first_name),
    customer_last_name: str(search.customer_last_name),
    customer_phone: str(search.customer_phone),
    customer_address1: str(search.customer_address1),
    customer_city: str(search.customer_city),
    customer_state: str(search.customer_state),
    customer_zip: str(search.customer_zip),
    call_summary: str(search.call_summary),
    loss_description: str(search.loss_description),
    call_log_id: str(search.call_log_id),
    from: str(search.from),
  }),
  component: CreateJobPage,
});

/** Build the initial form, folding in any preload search params (seed). */
function makeSeededForm(receivedByUserId: string | undefined, seed: NewJobSearch): CreateJobForm {
  const base = makeInitialForm(receivedByUserId);
  // ?from= means "same loss, different customer" — the customer block must stay
  // empty, so the customer_* seeds are ignored and the job bundle fills the rest
  // in once it loads.
  if (seed.from) return base;
  const hasSeed = Object.values(seed).some(Boolean);
  if (!hasSeed) return base;
  return {
    ...base,
    customer_first_name: seed.customer_first_name ?? base.customer_first_name,
    customer_last_name: seed.customer_last_name ?? base.customer_last_name,
    customer_address1: seed.customer_address1 ?? base.customer_address1,
    customer_city: seed.customer_city ?? base.customer_city,
    customer_state: seed.customer_state ?? base.customer_state,
    customer_zip: seed.customer_zip ?? base.customer_zip,
    customer_phones: {
      ...base.customer_phones,
      primary: seed.customer_phone ?? base.customer_phones.primary,
    },
    job_description: seed.call_summary ?? seed.loss_description ?? base.job_description,
  };
}

function CreateJobPage() {
  const auth = useAuth();
  const nav = useNavigate();
  const seed = Route.useSearch();
  const [form, setForm] = useState<CreateJobForm>(() => makeSeededForm(auth.user?.id, seed));
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);

  // Lookups
  const { data: staff }      = useQuery({ queryKey: ['profiles_active'],     queryFn: async () => (await supabase.from('profiles').select('user_id, display_name').eq('is_active', true).order('display_name')).data ?? [] });
  const { data: compPlans }  = useQuery({ queryKey: ['compensation_plans'],  queryFn: async () => (await supabase.from('compensation_plans').select('id, name').eq('is_active', true).order('sort_order')).data ?? [] });
  const { data: offices }    = useQuery({ queryKey: ['offices'],             queryFn: async () => (await supabase.from('offices').select('office_key, label').eq('is_active', true).order('sort_order')).data ?? [] });
  useQuery({ queryKey: ['job_statuses'],        queryFn: async () => (await supabase.from('job_statuses').select('status_key, label').eq('is_active', true).order('sort_order')).data ?? [] });

  // Auto-set received_by once auth is loaded
  useEffect(() => {
    if (auth.user?.id && !form.received_by) setForm((f) => ({ ...f, received_by: auth.user!.id }));
  }, [auth.user?.id]);

  // FB18 — ?from=<jobNumber>: copy the source job's loss facts in once, the
  // moment its bundle lands. Guarded by `prefilled` so a background refetch
  // can never stomp on what the user has typed since.
  const fromJob = useJob(seed.from ?? '');
  const [prefilled, setPrefilled] = useState(false);
  useEffect(() => {
    if (!seed.from || prefilled || !fromJob.data) return;
    setForm((f) => applyJobPrefill(f, fromJob.data));
    setPrefilled(true);
  }, [seed.from, prefilled, fromJob.data]);

  function patch(p: Partial<CreateJobForm>) { setForm((f) => ({ ...f, ...p })); }

  async function submit(thenAction: 'goto' | 'reset') {
    const errs = validate(form);
    setErrors(errs);
    setServerError(null);
    if (errs.length > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiSend<{ jobNumbers: string[] }>(
        '/api/jobs/create', 'POST',
        buildCreatePayload(form, auth.user?.id ?? null, seed.call_log_id),
      );
      if (thenAction === 'goto') {
        nav({ to: '/jobs/$jobNumber', params: { jobNumber: res.jobNumbers[0] } });
      } else {
        setForm(makeInitialForm(auth.user?.id));
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (e) {
      setServerError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  // The structure the assessor lookup (FB27) and the Job Size model care about
  // is the LOSS location — the job address, or the customer's own when the two
  // are the same. Identical resolution to CustomerCard's.
  const propertyAddress: PropertyAddress = form.job_address_same_as_customer
    ? {
      address1: form.customer_address1.trim(),
      city: form.customer_city, state: form.customer_state, zip: form.customer_zip,
    }
    : {
      address1: form.job_address1.trim(),
      city: form.job_city, state: form.job_state, zip: form.job_zip,
    };

  // FB23 — a NANP area code pins the number to exactly one state, so the phone
  // typed above suggests (never writes) the State.
  const suggestedState = stateFromPhones({ ...form.customer_phones });

  // FB37 — "the customer rang the moment it happened" is the common case.
  const receivedDateOnly = toDateInput(form.date_call_received, false);

  return (
    <div className="px-6 py-6 max-w-5xl mx-auto space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-[var(--color-navy)]">Create Job</h1>
          <p className="text-sm text-[var(--color-ink-soft)] mt-1">
            Required fields are marked with *. Job number(s) generate automatically from the chosen division(s).
          </p>
        </div>
        <div className="text-right text-xs text-[var(--color-ink-muted)]">
          <div className="uppercase tracking-wide">Date Call Received</div>
          <div className="text-sm font-semibold text-[var(--color-ink)]">
            {formatDateTime(form.date_call_received)}
          </div>
        </div>
      </div>

      {/* FB18 — associated-job banner. Says plainly what got copied and what
          the user still has to type, so nobody submits the tenant's details
          twice under a new job number. */}
      {seed.from && (
        <div className="rounded border border-[var(--color-navy)] bg-[var(--color-blue-tint)] p-3 text-sm">
          {fromJob.isError ? (
            <>
              <strong>Couldn't load {seed.from}.</strong>{' '}
              {(fromJob.error as Error)?.message ?? 'Fill the form in manually.'}
            </>
          ) : !prefilled ? (
            <>Loading job information from {seed.from}…</>
          ) : (
            <>
              <strong>Creating associated job from {seed.from}</strong> — enter the new
              customer's information.
              <div className="mt-1 text-xs text-[var(--color-ink-soft)]">
                Division, loss address, loss details, claim / policy information and
                participants were copied over. Occupancy is deliberately blank — this
                party has the other role. Everything is still editable.
              </div>
            </>
          )}
        </div>
      )}

      {errors.length > 0 && (
        <div className="rounded p-3 bg-[var(--color-danger-tint)] text-[var(--color-danger)] text-sm">
          <strong>Fix these before saving:</strong>
          <ul className="list-disc ml-5 mt-1">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}
      {serverError && (
        <div className="rounded p-3 bg-[var(--color-danger-tint)] text-[var(--color-danger)] text-sm">
          <strong>Save failed:</strong> {serverError}
        </div>
      )}

      {/* 1. Customer Information ---------------------------------------------- */}
      <Section title="1. Customer Information"
        action={
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <input type="radio" checked={!form.is_company_customer}
                onChange={() => patch({ is_company_customer: false })} />
              Individual
            </label>
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <input type="radio" checked={form.is_company_customer}
                onChange={() => patch({ is_company_customer: true })} />
              Company
            </label>
          </div>
        }>
        <CustomerSearchInline
          currentCustomerId={form.customer_id}
          onPick={(picked) => {
            if (!picked) {
              patch({ customer_id: null });
              return;
            }
            const c = picked.data;
            const phones = (c.phones ?? {}) as Record<string, string>;
            const emails = (c.emails ?? {}) as Record<string, string>;
            const parts = customerNameParts(c.first_name, c.last_name, c.name);
            patch({
              customer_id: picked.id,
              customer_first_name: parts.first,
              customer_last_name: parts.last,
              customer_address1: c.address1 ?? '',
              customer_city: c.city ?? '',
              customer_state: c.state ?? '',
              customer_zip: c.zip ?? '',
              customer_email: emails.primary ?? '',
              customer_secondary_email: emails.secondary ?? '',
              // Slot names per @pdq/core's PHONE_SLOT_ORDER; `main` is only read
              // because rows created by the OLD version of this form used it.
              customer_phones: {
                primary: phones.primary ?? phones.main ?? '',
                secondary: phones.secondary ?? phones.business ?? '',
                mobile: phones.mobile ?? phones.cell ?? '',
                home: phones.home ?? '',
                work: phones.work ?? '',
                other: phones.other ?? '',
              },
            });
          }}
        />

        {form.is_company_customer ? (
          <Field label="Company Name *" htmlFor="cust_co">
            <Input id="cust_co" required={!form.customer_id} value={form.customer_company}
              onChange={(e) => patch({ customer_company: e.target.value })} />
          </Field>
        ) : (
          /* The job page's Customer Information card edits these as two separate
             fields; `customers.name` is recomposed "Last, First" on save. The
             old "Title" dropdown is gone — `customers` has no title column, so
             nothing typed into it was ever saved. */
          <Grid cols={2}>
            <Field label="First Name *" htmlFor="cust_fn">
              <Input id="cust_fn" required={!form.customer_id}
                value={form.customer_first_name}
                onChange={(e) => patch({ customer_first_name: e.target.value })} />
            </Field>
            <Field label="Last Name *" htmlFor="cust_ln">
              <Input id="cust_ln" required={!form.customer_id}
                value={form.customer_last_name}
                onChange={(e) => patch({ customer_last_name: e.target.value })} />
            </Field>
          </Grid>
        )}

        <Grid cols={2}>
          <Field label="Email" htmlFor="cust_em">
            <Input id="cust_em" type="email" value={form.customer_email}
              onChange={(e) => patch({ customer_email: e.target.value })} />
          </Field>
          <Field label="Secondary Email" htmlFor="cust_em2">
            <Input id="cust_em2" type="email" value={form.customer_secondary_email}
              onChange={(e) => patch({ customer_secondary_email: e.target.value })} />
          </Field>
        </Grid>

        {/* FB52 — "Street", matching the job page: the town belongs in City,
            which is the next box along. */}
        <Field label="Street *" htmlFor="cust_addr"
          hint="Number and street only — the town goes in City.">
          <Input id="cust_addr" required={!form.customer_id}
            value={form.customer_address1}
            onChange={(e) => patch({ customer_address1: e.target.value })} />
        </Field>
        {/* No Country box: `customers` has no country column, so the old one
            silently discarded whatever was typed into it. */}
        <Grid cols={3}>
          <Field label="ZIP" htmlFor="cust_zip">
            <Input id="cust_zip" value={form.customer_zip}
              onChange={(e) => patch({ customer_zip: e.target.value })} />
          </Field>
          <Field label="City" htmlFor="cust_city">
            <Input id="cust_city" value={form.customer_city}
              onChange={(e) => patch({ customer_city: e.target.value })} />
          </Field>
          <Field label="County / Region" htmlFor="cust_co_">
            <Input id="cust_co_" value={form.customer_county}
              onChange={(e) => patch({ customer_county: e.target.value })} />
          </Field>
        </Grid>

        {/* FB23 — the State the caller's area code implies, offered once and
            never written over something typed. */}
        <Field label="State / Province *" htmlFor="cust_state">
          <Input id="cust_state" required={!form.customer_id} value={form.customer_state}
            onChange={(e) => patch({ customer_state: e.target.value })} />
        </Field>
        {!form.customer_state.trim() && suggestedState && (
          <button type="button"
            title={`Area code of the phone on this job is a ${suggestedState} area code — click to use it`}
            onClick={() => patch({ customer_state: suggestedState })}
            className="-mt-1 inline-flex w-fit items-center gap-1 rounded-full bg-[var(--color-blue-tint)] px-2.5 py-0.5 text-[11px] font-semibold text-[var(--color-blue-deep)] hover:brightness-95">
            {suggestedState} from phone · Use
          </button>
        )}

        <div className="border-t border-[var(--color-line)] pt-3 mt-3">
          <div className="text-xs uppercase tracking-wide font-bold text-[var(--color-ink-soft)] mb-2">
            Phone Numbers
          </div>
          <PhonesEditor value={form.customer_phones}
            onChange={(p: PhoneSet) => patch({ customer_phones: p })} />
        </div>

        {/* FB27 / FB24 / FB25 / FB18 — the same four rows, in the same order,
            as the bottom of the job page's Customer Information card. */}
        <div className="border-t border-[var(--color-line)] pt-3 mt-3 space-y-1">
          <YearBuiltRow
            value={form.year_built}
            address={propertyAddress}
            onSave={(v) => patch({ year_built: v })} />
          <ToggleRow
            label="Self Pay"
            on={form.self_pay}
            hot
            title={form.self_pay
              ? 'On — the customer pays directly. Switch off to bill the carrier.'
              : 'Off — billed through the carrier. Switch on to make this a self pay job.'}
            onToggle={() => patch({ self_pay: !form.self_pay })} />
          <BillingAddressToggle
            value={form}
            existingCustomer={!!form.customer_id}
            onChange={patch} />
          <div className="grid grid-cols-[140px_1fr] gap-3 py-1 items-center">
            <div className="text-xs uppercase tracking-wide text-[var(--color-ink-muted)]">
              Occupancy
            </div>
            <OccupancyChoice
              value={form.occupancy_role || null}
              onPick={(v) => patch({ occupancy_role: occupancyOf(v) })} />
          </div>
          {form.occupancy_role === 'tenant' && (
            <p className="text-xs text-[var(--color-ink-muted)] pl-[152px]">
              Tenant — the landlord's loss is a separate job file on the same
              property. Create it from this job once it's saved.
            </p>
          )}
        </div>

        <BillingAddressFields value={form} onChange={patch} />
      </Section>

      {/* 2. Job Address Information ------------------------------------------ */}
      <Section title="2. Job Address Information"
        action={
          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
            <input type="checkbox" checked={form.job_address_same_as_customer}
              onChange={(e) => patch({ job_address_same_as_customer: e.target.checked })} />
            Same as customer address
          </label>
        }>
        {form.job_address_same_as_customer ? (
          <div className="text-sm text-[var(--color-ink-muted)]">
            Job address will be copied from customer address on save.
          </div>
        ) : (
          <>
            <Field label="Address" htmlFor="job_addr">
              <Input id="job_addr" value={form.job_address1}
                onChange={(e) => patch({ job_address1: e.target.value })} />
            </Field>
            {/* jobs has loss_address1/city/state/zip/county and nothing else —
                no country, and no per-site phone bag. The Country box and the
                whole "Phone Numbers (job site)" block that used to sit here were
                never transmitted by the endpoint and are gone. */}
            <Grid cols={4}>
              <Field label="ZIP" htmlFor="job_zip">
                <Input id="job_zip" value={form.job_zip}
                  onChange={(e) => patch({ job_zip: e.target.value })} />
              </Field>
              <Field label="City" htmlFor="job_city">
                <Input id="job_city" value={form.job_city}
                  onChange={(e) => patch({ job_city: e.target.value })} />
              </Field>
              <Field label="County / Region" htmlFor="job_co_">
                <Input id="job_co_" value={form.job_county}
                  onChange={(e) => patch({ job_county: e.target.value })} />
              </Field>
              <Field label="State / Province" htmlFor="job_state">
                <Input id="job_state" value={form.job_state}
                  onChange={(e) => patch({ job_state: e.target.value })} />
              </Field>
            </Grid>
          </>
        )}
      </Section>

      {/* 3. Division (multi-select) ------------------------------------------ */}
      <Section title="3. Division *"
        hint={
          form.divisions.length > 1
            ? `One job with ${form.divisions.length} divisions — a single job number, each division tracked on the same job.`
            : 'Pick at least one. Multiple selections add divisions to the same job (one job number).'
        }
        action={form.divisions.length > 0 && <StatusPill kind="info">{form.divisions.length} selected</StatusPill>}>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          {DIVISIONS.map((d) => {
            const checked = form.divisions.includes(d);
            return (
              <label key={d} className={
                'flex items-center gap-2 px-3 py-2 rounded border text-sm cursor-pointer transition-colors '
                + (checked
                  ? 'border-[var(--color-navy)] bg-[var(--color-blue-tint)]'
                  : 'border-[var(--color-line)] hover:bg-[var(--color-surface-1)]')
              }>
                <input type="checkbox" checked={checked}
                  onChange={(e) => patch({
                    divisions: e.target.checked
                      ? [...form.divisions, d]
                      : form.divisions.filter((x) => x !== d),
                  })} />
                <span className="font-mono text-xs text-[var(--color-ink-muted)]">{d}</span>
                <span>{DIVISION_LABELS[d]}</span>
              </label>
            );
          })}
        </div>
      </Section>

      {/* 4. Job Information -------------------------------------------------- */}
      <Section title="4. Job Information">
        <Grid cols={2}>
          <Field label="Call Taker (Received By)" htmlFor="rby">
            <Select id="rby" value={form.received_by}
              onChange={(e) => patch({ received_by: e.target.value })}>
              <option value="">—</option>
              {(staff ?? []).map((s) => (
                <option key={s.user_id} value={s.user_id}>{s.display_name}</option>
              ))}
            </Select>
          </Field>
          <Field label="Job Name" htmlFor="jname">
            <Input id="jname" value={form.job_name}
              onChange={(e) => patch({ job_name: e.target.value })}
              placeholder="e.g. Smith — basement water" />
          </Field>
          <Field label="Office Name *" htmlFor="off">
            <Select id="off" required value={form.office_name}
              onChange={(e) => patch({ office_name: e.target.value })}>
              <option value="">—</option>
              {(offices ?? []).map((o) => (
                <option key={o.office_key} value={o.office_key}>{o.label}</option>
              ))}
            </Select>
          </Field>
          <Field label="Loss Category *" htmlFor="lc">
            <Select id="lc" required value={form.loss_category}
              onChange={(e) => patch({ loss_category: e.target.value })}>
              <option value="">—</option>
              <option>Residential</option><option>Commercial</option><option>Multi-Family</option><option>Industrial</option>
            </Select>
          </Field>
        </Grid>

        {/* FB29b / FB31 / FB46 — the same admin-backed dropdowns as the job
            page's Job Information card, in the same 2×2 arrangement. */}
        <div className="grid gap-x-8 gap-y-1 sm:grid-cols-2">
          <LeadChannelRow value={form.reported_by}
            onSave={(v) => patch({ reported_by: v, referred_by: '' })} />
          <LeadSourceRow channel={form.reported_by} value={form.referred_by}
            onSave={(v) => patch({ referred_by: v })} />
          <DamageCauseRow value={form.job_source}
            onSave={(v) => patch({ job_source: v })} />
          <div className="grid grid-cols-[140px_1fr] gap-3 py-1 items-center">
            <div className="text-xs uppercase tracking-wide text-[var(--color-ink-muted)]">
              Type of Loss
            </div>
            <Select value={lossTypeSelectValue(form.loss_type)} className="py-1 text-sm"
              onChange={(e) => {
                if (e.target.value !== LEGACY_LOSS_TYPE) patch({ loss_type: e.target.value });
              }}>
              <option value="">—</option>
              {lossTypeSelectValue(form.loss_type) === LEGACY_LOSS_TYPE && (
                <option value={LEGACY_LOSS_TYPE}>{form.loss_type}</option>
              )}
              {LOSS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </Select>
          </div>
          <div className="grid grid-cols-[140px_1fr] gap-3 py-1 items-center">
            <div className="text-xs uppercase tracking-wide text-[var(--color-ink-muted)]">
              Secondary Loss Type
            </div>
            <Select value={lossTypeSelectValue(form.secondary_loss_type)} className="py-1 text-sm"
              onChange={(e) => {
                if (e.target.value !== LEGACY_LOSS_TYPE) patch({ secondary_loss_type: e.target.value });
              }}>
              <option value="">—</option>
              {lossTypeSelectValue(form.secondary_loss_type) === LEGACY_LOSS_TYPE && (
                <option value={LEGACY_LOSS_TYPE}>{form.secondary_loss_type}</option>
              )}
              {LOSS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </Select>
          </div>
        </div>

        <Grid cols={3}>
          {/* FB37 — most callers ring the moment it happens, so the common
              case is one click rather than a date picker. */}
          <Field label="Date of Loss" htmlFor="dol">
            <Input id="dol" type="date" value={form.date_of_loss}
              onChange={(e) => patch({ date_of_loss: e.target.value })} />
          </Field>
          <div className="flex items-end pb-1">
            {form.date_of_loss !== receivedDateOnly && (
              <button type="button"
                onClick={() => patch({ date_of_loss: receivedDateOnly })}
                className="inline-flex items-center gap-1 rounded-full bg-[var(--color-blue-tint)] px-2.5 py-1 text-[11px] font-semibold text-[var(--color-blue-deep)] hover:brightness-95">
                Same as Date Call Received
              </button>
            )}
          </div>
          <div />
          <Field label="Custom Code" htmlFor="cc">
            <Input id="cc" value={form.custom_code}
              onChange={(e) => patch({ custom_code: e.target.value })} />
          </Field>
          <Field label="CAT Reference" htmlFor="cat">
            <Input id="cat" value={form.cat_reference}
              onChange={(e) => patch({ cat_reference: e.target.value })} />
          </Field>
        </Grid>

        {/* FB16 / FB34 / FB35 — the scoping quick facts and the live Job Size
            suggestion, exactly as the job page shows them. */}
        <div className="border-t border-[var(--color-line)] pt-3 mt-1">
          <ScopingFacts
            value={form}
            onChange={patch}
            division={form.divisions[0] ?? null}
            typeOfLoss={form.loss_type}
            sourceOfLoss={form.job_source}
            selfPay={form.self_pay} />
        </div>
      </Section>

      {/* 5. Dispatch --------------------------------------------------------- */}
      <Section title="5. Dispatch">
        <CreateDispatchRow mode={form.dispatch} onChange={patch} />
      </Section>

      {/* 6. Initial Call Summary --------------------------------------------- */}
      <Section title="6. Initial Call Summary">
        {/* ─── RingCentral prefill seam (FB14) ───────────────────────────────
            The dispatch call popup already passes the AI call summary in as
            ?loss_description=, which lands here. When the `ringcentral-*` port
            adds jobs.rc_call_id at creation, the transcript fills this box the
            same way — and, per FB14's rule, must never overwrite words a human
            has typed. Nothing else here changes: it stays a plain textarea over
            jobs.job_description, which is the field the job page renders next
            to the call recording and transcript. */}
        <Field label="Initial Call Summary" htmlFor="ics"
          hint={`What the customer said on the call. ${form.job_description.length} / 1024`}>
          <Textarea id="ics" maxLength={1024} rows={5} value={form.job_description}
            onChange={(e) => patch({ job_description: e.target.value })} />
        </Field>
        <Field label="Special Instructions" htmlFor="si" hint={`${form.special_instructions.length} / 1024`}>
          <Textarea id="si" maxLength={1024} rows={3} value={form.special_instructions}
            onChange={(e) => patch({ special_instructions: e.target.value })} />
        </Field>
      </Section>

      {/* 7. Internal Participants -------------------------------------------- */}
      <Section title="7. Internal Participants"
        hint="Each role's compensation plan is recorded so commission reports work later.">
        <div className="space-y-2">
          {INTERNAL_ROLE_KEYS.map((rk) => (
            <Grid key={rk} cols={3}>
              <div className="flex items-center text-sm font-semibold capitalize">
                {prettyRole(rk)}
              </div>
              <Field label="Staff" htmlFor={`int_${rk}_s`}>
                <Select id={`int_${rk}_s`} value={form.internal[rk].staff_user_id}
                  onChange={(e) => patch({
                    internal: { ...form.internal, [rk]: { ...form.internal[rk], staff_user_id: e.target.value } },
                  } as Partial<CreateJobForm>)}>
                  <option value="">— unassigned —</option>
                  {(staff ?? []).map((s) => (
                    <option key={s.user_id} value={s.user_id}>{s.display_name}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Compensation Plan" htmlFor={`int_${rk}_c`}>
                <Select id={`int_${rk}_c`}
                  disabled={!form.internal[rk].staff_user_id}
                  value={form.internal[rk].compensation_plan_id}
                  onChange={(e) => patch({
                    internal: { ...form.internal, [rk]: { ...form.internal[rk], compensation_plan_id: e.target.value } },
                  } as Partial<CreateJobForm>)}>
                  <option value="">— none —</option>
                  {(compPlans ?? []).map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </Select>
              </Field>
            </Grid>
          ))}
        </div>
      </Section>

      {/* 8. External Participants -------------------------------------------- */}
      <Section title="8. External Participants"
        hint="Click the + next to any slot to create a new external contact inline.">
        <Grid cols={2}>
          {EXTERNAL_ROLE_SLOTS.map((slot) => (
            <ExternalContactPicker key={slot.key}
              label={slot.label}
              value={form.external[slot.key]}
              onChange={(c) => patch({ external: { ...form.external, [slot.key]: c } } as Partial<CreateJobForm>)}
              roleHint={slot.label} />
          ))}
        </Grid>
      </Section>

      {/* 9. Policy Information ----------------------------------------------- */}
      <Section title="9. Policy Information">
        <Grid cols={3}>
          <Field label="Claim Number" htmlFor="cl_n">
            <Input id="cl_n" value={form.claim_number}
              onChange={(e) => patch({ claim_number: e.target.value })} />
          </Field>
          <Field label="File Number" htmlFor="fl_n">
            <Input id="fl_n" value={form.file_number}
              onChange={(e) => patch({ file_number: e.target.value })} />
          </Field>
          <Field label="Policy Number" htmlFor="pol_n">
            <Input id="pol_n" value={form.policy_number}
              onChange={(e) => patch({ policy_number: e.target.value })} />
          </Field>
          <Field label="Policy Start Date" htmlFor="psd">
            <Input id="psd" type="date" value={form.policy_start_date}
              onChange={(e) => patch({ policy_start_date: e.target.value })} />
          </Field>
          <Field label="Policy Expiration Date" htmlFor="ped">
            <Input id="ped" type="date" value={form.policy_expiration_date}
              onChange={(e) => patch({ policy_expiration_date: e.target.value })} />
          </Field>
          {/* FB47 — the job page's Claim card edits this; intake could not set
              it, so every claim started with a blank policy limit. */}
          <Field label="Policy Limit" htmlFor="pl">
            <Input id="pl" type="number" step="0.01" value={form.policy_limit}
              onChange={(e) => patch({ policy_limit: e.target.value })} />
          </Field>
        </Grid>
      </Section>

      {/* 10. Payment Services ------------------------------------------------ */}
      <Section title="10. Payment Services"
        hint="Self Pay lives with the customer, above — it decides whether any of this applies.">
        <Grid cols={3}>
          <Field label="Deductible Required" htmlFor="dr">
            <Select id="dr" value={form.deductible_required}
              onChange={(e) => patch({ deductible_required: e.target.value as CreateJobForm['deductible_required'] })}>
              <option value="TBD">TBD</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </Select>
          </Field>
          <Field label="Amount" htmlFor="da">
            <Input id="da" type="number" step="0.01" value={form.deductible_amount}
              disabled={form.deductible_required !== 'Yes'}
              onChange={(e) => patch({ deductible_amount: e.target.value })} />
          </Field>
          <Field label="Collect When" htmlFor="dcw">
            <Select id="dcw" value={form.deductible_collect_when}
              disabled={form.deductible_required !== 'Yes'}
              onChange={(e) => patch({ deductible_collect_when: e.target.value })}>
              <option value="">—</option>
              <option>At start of job</option>
              <option>At completion</option>
              <option>Final payment</option>
              <option>Other</option>
            </Select>
          </Field>
          <Field label="Dwelling Limits" htmlFor="dwl">
            <Input id="dwl" type="number" step="0.01" value={form.dwelling_limit}
              onChange={(e) => patch({ dwelling_limit: e.target.value })} />
          </Field>
          <Field label="Contents Limits" htmlFor="conl">
            <Input id="conl" type="number" step="0.01" value={form.contents_limit}
              onChange={(e) => patch({ contents_limit: e.target.value })} />
          </Field>
          <Field label="Other Structures Limits" htmlFor="osl">
            <Input id="osl" type="number" step="0.01" value={form.other_structures_limit}
              onChange={(e) => patch({ other_structures_limit: e.target.value })} />
          </Field>
        </Grid>
      </Section>

      {/* Bottom buttons ------------------------------------------------------ */}
      <div className="sticky bottom-0 bg-[var(--color-surface-1)] py-3 border-t border-[var(--color-line)] -mx-6 px-6 flex flex-wrap items-center justify-end gap-2 shadow-[0_-4px_12px_rgba(0,0,0,0.04)]">
        <span className="text-xs text-[var(--color-ink-muted)] mr-auto">
          {form.divisions.length > 1 ? `Will create one job with ${form.divisions.length} divisions.` : ''}
        </span>
        <Button type="button" variant="ghost" disabled={submitting}
          onClick={() => submit('reset')}>
          {submitting ? 'Saving…' : 'Save & Create New Job'}
        </Button>
        <Button type="button" variant="primary" disabled={submitting}
          onClick={() => submit('goto')}>
          {submitting ? 'Saving…' : 'Save & Go to Slideboard'}
        </Button>
      </div>
    </div>
  );
}

function prettyRole(rk: string): string {
  return rk.replace(/_/g, ' ');
}
